import { normalizeApiBase } from "../api";
import { ControlPlaneError } from "../api";
import { CsrfTokenError, csrfTokenCacheFor } from "../csrf";
import { parseSourceItemsResponse, parseSourceTextResponse } from "./data";
import type { SourceItemsResult, SourceKind, SourceTextResult } from "./data";

/**
 * Slice-local client for the two `/v1/knowledge` reads the collected-data
 * library needs. It mirrors the shared control-plane client's cookie + CSRF +
 * bounded-fetch discipline (the knowledge boundary is a POST envelope, so a
 * cookie-authenticated read passes the same CSRF guard a mutation does) but
 * stays in this slice so `api.ts` is untouched.
 *
 * `asOf` is the caller-supplied coverage cut-off and is never substituted by a
 * wall clock inside this module: a page that re-stamped `asOf` per request would
 * silently invalidate its own cursor chain.
 */

const REQUEST_TIMEOUT_MS = 15_000;

/** One page of the inventory. The server's own ceiling is 100. */
export const SOURCE_ITEMS_PAGE_SIZE = 50;
/** One page of a document's blocks. The server's own ceiling is 50. */
export const SOURCE_TEXT_PAGE_SIZE = 50;

async function boundedFetch(
  input: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const external = init.signal;
  let timedOut = false;
  const forward = () => controller.abort(external?.reason);
  if (external?.aborted) forward();
  else external?.addEventListener("abort", forward, { once: true });
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (reason) {
    if (timedOut) {
      throw new ControlPlaneError("The collected-data request timed out.");
    }
    if (reason instanceof Error) throw reason;
    throw new ControlPlaneError("The collected-data request failed.");
  } finally {
    globalThis.clearTimeout(timeout);
    external?.removeEventListener("abort", forward);
  }
}

async function responseError(response: Response): Promise<ControlPlaneError> {
  const requestId = response.headers.get("x-request-id");
  let code: string | undefined;
  try {
    const payload: unknown = await response.clone().json();
    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string" &&
      /^[a-z][a-z0-9_]{0,127}$/u.test(payload.error)
    ) {
      code = payload.error;
    }
  } catch {
    // The status and request id remain useful when an error body is absent.
  }
  return new ControlPlaneError(
    `The control plane returned ${response.status}.${
      requestId ? ` Request ${requestId}.` : ""
    }`,
    response.status,
    code,
  );
}

async function post(
  apiBase: string,
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  const origin = normalizeApiBase(apiBase);
  if (!origin) {
    throw new ControlPlaneError("The control-plane API origin is invalid.");
  }
  const send = (csrf: string): Promise<Response> =>
    boundedFetch(`${origin}${path}`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "csrf-token": csrf,
      },
      body: JSON.stringify(body),
      signal,
    });
  let response: Response;
  try {
    response = await csrfTokenCacheFor(origin).send(send);
  } catch (error) {
    if (error instanceof CsrfTokenError) {
      throw new ControlPlaneError(error.message, error.status);
    }
    throw error;
  }
  if (!response.ok) throw await responseError(response);
  return response.json();
}

export interface SourceItemsQuery {
  readonly asOf: string;
  readonly sourceKinds?: readonly SourceKind[] | null;
  readonly textStatuses?: readonly string[] | null;
  readonly cursor?: string | null;
  readonly limit?: number;
}

export async function listSourceItems(
  apiBase: string,
  query: SourceItemsQuery,
  signal?: AbortSignal,
): Promise<SourceItemsResult> {
  const payload = await post(
    apiBase,
    "/v1/knowledge/list_source_items",
    {
      asOf: query.asOf,
      // `null` is the only way to say "every value"; the boundary refuses an
      // empty array rather than reading it as no filter.
      sourceKinds: query.sourceKinds?.length ? [...query.sourceKinds] : null,
      textStatuses: query.textStatuses?.length ? [...query.textStatuses] : null,
      limit: query.limit ?? SOURCE_ITEMS_PAGE_SIZE,
      cursor: query.cursor ?? null,
    },
    signal,
  );
  return parseSourceItemsResponse(payload);
}

export async function fetchSourceText(
  apiBase: string,
  query: {
    readonly asOf: string;
    readonly documentVersionId: string;
    readonly cursor?: string | null;
    readonly limit?: number;
  },
  signal?: AbortSignal,
): Promise<SourceTextResult> {
  const payload = await post(
    apiBase,
    "/v1/knowledge/fetch",
    {
      asOf: query.asOf,
      documentVersionId: query.documentVersionId,
      limit: query.limit ?? SOURCE_TEXT_PAGE_SIZE,
      cursor: query.cursor ?? null,
    },
    signal,
  );
  return parseSourceTextResponse(payload);
}
